import { useCallback } from 'react';
import { OnNodesChange, OnEdgesChange, NodeChange, EdgeChange, Node } from 'reactflow';

import useNodeLogging from '../../hooks/useNodeLogging';
import { snapPositionToGrid, snapDimensionsToGrid } from '../../utils/grid';
import { CustomNode } from '../../types';

type CanvasMode = 'project' | 'playbook';

// Define the props the hook needs
export interface ChangeHandlerProps { // Export the type
    isEmbedded: boolean;
    selectedNodeIdRef: React.MutableRefObject<string | null>;
    mode: CanvasMode;
    nodes: Node[]; // Use generic Node
    onNodesChange?: OnNodesChange; // Action from store
    onEdgesChange?: OnEdgesChange; // Action from store
    // Accept the union of function types provided by the selector
    setSelectedNode?: ((node: CustomNode | null) => void) | ((node: Node | null) => void);
}

interface ChangeHandlers {
    handleNodesChange: OnNodesChange;
    handleEdgesChange: OnEdgesChange;
}

export const useChangeHandlers = ({
    isEmbedded,
    selectedNodeIdRef,
    mode,
    nodes: allNodes, // Rename for clarity
    onNodesChange: onNodesChangeFromStore, // Rename for clarity
    onEdgesChange: onEdgesChangeFromStore, // Rename for clarity
    setSelectedNode, // Action from props
}: ChangeHandlerProps): ChangeHandlers => {
    const { onNodesChange: logNodeChanges } = useNodeLogging();

    const handleNodesChange: OnNodesChange = useCallback((changes: NodeChange[]) => {
        // Actions are now passed via props
        if (!onNodesChangeFromStore || !setSelectedNode) {
             console.error("[Canvas] Missing required store actions for node change handler in mode:", mode);
             return;
        }

        // Apply grid snapping to position and dimension changes
        const processedChanges = changes.map(change => {
            if (change.type === 'position' && change.position && !change.dragging) {
                return { ...change, position: snapPositionToGrid(change.position) };
            }
            if (change.type === 'dimensions' && change.dimensions) {
                return { ...change, dimensions: snapDimensionsToGrid(change.dimensions) };
            }
            return change;
        });

        // --- Handle selection update during drag ---
        const dragChange = processedChanges.find(change =>
            change.type === 'position' && 'dragging' in change && change.dragging
        );

        if (dragChange && 'id' in dragChange) {
            const nodeId = dragChange.id;
            if (selectedNodeIdRef.current !== nodeId) {
                const draggedNode = allNodes.find((node: Node) => node.id === nodeId);
                if (draggedNode) {
                    selectedNodeIdRef.current = nodeId;
                    setSelectedNode(draggedNode as any);
                }
            }
        }
        // --- End selection update during drag ---

        // Apply changes to the store, potentially filtering based on embedded mode
        if (!isEmbedded) {
            // logNodeChanges(processedChanges); // Consider conditional logging
            onNodesChangeFromStore(processedChanges);
        } else {
            const selectionChanges = processedChanges.filter(
                change => change.type === 'select'
            );
            if (selectionChanges.length > 0) {
                onNodesChangeFromStore(selectionChanges);
            }
        }
    }, [isEmbedded, mode, allNodes, onNodesChangeFromStore, setSelectedNode, selectedNodeIdRef, logNodeChanges]); // Update dependencies

    const handleEdgesChange: OnEdgesChange = useCallback((changes: EdgeChange[]) => {
        // Action is now passed via props
        if (!onEdgesChangeFromStore) {
            console.error("[Canvas] Missing onEdgesChange action in store for mode:", mode);
            return;
        }

        if (!isEmbedded) {
            onEdgesChangeFromStore(changes);
        }
    }, [isEmbedded, mode, onEdgesChangeFromStore]); // Update dependencies

    return {
        handleNodesChange,
        handleEdgesChange,
    };
};
